/* eslint-disable */
import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import {
  Grid,
  Stack,
  Typography,
  Button,
  Modal,
  TextField,
} from "@mui/material";
import { useMediaQuery, useTheme } from "@mui/material";
import { useSelector } from "react-redux";
import { useParams } from "react-router";
import { DownloadIcon } from "lucide-react";
import axios from "axios";

// Component imports
import ScanningStatsCard from "./components/ScanningStatsCard";
import ExpiryStatsCard from "./components/ExpiryStatsCard";
import RevenueImpactCard from "./components/RevenueImpactCard";
import LineChartToggle from "./lineChartToggle";
import NearingExpiryCard from "./components/NearingExpiryCard";
import UserThroughputCard from "./components/ThroughputCard";

// Data imports
import {
  scanningStats,
  expiryStats,
  productCategories,
} from "../../../data/insightData";

// API imports
import { fetchAndDownloadCSV, createSapData } from "api/overView";

function Overview({ dates }) {
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));

  const [scanStats, setScanStats] = useState(scanningStats);
  const [expStats, setExpStats] = useState(expiryStats);
  const [prodCategories, setProdCategories] = useState(productCategories);
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(false);

  const [openModal, setOpenModal] = useState(false);
  const [formData, setFormData] = useState({
    Code: "",
    Product_Description: "",
    Vendor: "",
    UOM: "",
    MAP: "",
  });
  const [csvFile, setCsvFile] = useState(null);

  const selectedDate = useSelector((state) => state.customization.selectedDate);
  const { storeId } = useParams();

  useEffect(() => {
    const storedUserData = localStorage.getItem("userData");
    if (storedUserData) {
      setUserData(JSON.parse(storedUserData));
    }
  }, []);

  const fetchDownloadCSV = async () => {
    const abortController = new AbortController();
    setLoading(true);

    try {
      const response = await fetchAndDownloadCSV(
        userData.id,
        selectedDate || new Date(),
        abortController.signal
      );

      if (!response.data.data || !response.data.data.length) {
        throw new Error("No data available");
      }

      const csvData = convertToCSV(response.data.data);
      downloadCSV(
        csvData,
        `inventory_data_${new Date()
          .toISOString()
          .split("T")[0]
          .replace(/[-]/g, "_")}_${new Date()
          .toTimeString()
          .split(" ")[0]
          .replace(/:/g, "_")}.csv`
      );
    } catch (error) {
      if (axios.isCancel(error)) {
        console.log("Request cancelled");
      } else {
        console.error("Error fetching throughput data:", error);
      }
    } finally {
      if (!abortController.signal.aborted) {
        setLoading(false);
        abortController.abort();
      }
    }
  };

  const convertToCSV = (data) => {
    const headers = Object.keys(data[0]).join(",");
    const rows = data.map((obj) =>
      Object.values(obj)
        .map((value) => (value !== null ? `"${value}"` : '""'))
        .join(",")
    );
    return [headers, ...rows].join("\n");
  };

  const downloadCSV = (csvContent, fileName) => {
    const blob = new Blob([csvContent], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = fileName;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  // --- Modal Handlers ---
  const handleAddProduct = () => setOpenModal(true);
  const handleCloseModal = () => {
    setOpenModal(false);
    setFormData({
      Code: "",
      Product_Description: "",
      Vendor: "",
      UOM: "",
      MAP: "",
    });
    setCsvFile(null);
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleFileChange = (e) => {
    setCsvFile(e.target.files[0]);
  };

  const handleSubmit = async () => {
    const abortController = new AbortController();

    try {
      let payload = [];

      if (csvFile) {
        // Parse CSV file to JSON
        const text = await csvFile.text();
        const rows = text.split("\n").map((r) => r.split(","));
        const headers = rows[0];
        payload = rows.slice(1).map((row) => {
          const obj = {};
          headers.forEach((h, i) => {
            obj[h.trim()] = row[i]?.trim();
          });
          return obj;
        });
      } else {
        payload = formData;
      }

      const res = await createSapData(payload, abortController.signal);
      console.log("SAP created:", res);

      handleCloseModal();
    } catch (err) {
      console.error("Error creating SAP data:", err);
    }
  };

  return (
    <div className="w-full p-2">
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center">
            <Typography variant="h3">Overview</Typography>

            <Stack direction="row" spacing={1}>
              <Button
                variant="outlined"
                color="secondary"
                onClick={handleAddProduct}>
                Add Product
              </Button>

              {isSmallScreen ? (
                <Button
                  onClick={fetchDownloadCSV}
                  disabled={loading}
                  sx={{
                    minWidth: "auto",
                    padding: 0,
                    backgroundColor: "transparent",
                    "&:hover": { backgroundColor: "transparent" },
                  }}>
                  <DownloadIcon size={20} />
                </Button>
              ) : (
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<DownloadIcon />}
                  onClick={fetchDownloadCSV}
                  disabled={loading}>
                  {loading ? "Downloading..." : "Download"}
                </Button>
              )}
            </Stack>
          </Stack>
        </Grid>

        <Grid item xs={12}>
          <Grid container spacing={1.5}>
            <ScanningStatsCard />
            <NearingExpiryCard />
            <RevenueImpactCard />
            <UserThroughputCard />
          </Grid>
        </Grid>

        <Grid item xs={12}>
          <LineChartToggle dates={[selectedDate?.toISOString()]} />
        </Grid>
      </Grid>

      {/* Modal */}
      <Modal open={openModal} onClose={handleCloseModal}>
        <div className="bg-white p-6 rounded shadow-lg w-[400px] mx-auto mt-20">
          <Typography variant="h6" gutterBottom>
            Add SAP Product
          </Typography>

          <TextField
            name="Code"
            label="Code"
            fullWidth
            margin="dense"
            value={formData.Code}
            onChange={handleChange}
          />
          <TextField
            name="Product_Description"
            label="Product Description"
            fullWidth
            margin="dense"
            value={formData.Product_Description}
            onChange={handleChange}
          />
          <TextField
            name="Vendor"
            label="Vendor"
            fullWidth
            margin="dense"
            value={formData.Vendor}
            onChange={handleChange}
          />
          <TextField
            name="UOM"
            label="UOM"
            fullWidth
            margin="dense"
            value={formData.UOM}
            onChange={handleChange}
          />
          <TextField
            name="MAP"
            label="MAP"
            type="number"
            fullWidth
            margin="dense"
            value={formData.MAP}
            onChange={handleChange}
          />

          <input
            type="file"
            accept=".csv"
            onChange={handleFileChange}
            style={{ marginTop: "15px" }}
          />

          <Stack direction="row" spacing={2} justifyContent="flex-end" mt={3}>
            <Button onClick={handleCloseModal}>Cancel</Button>
            <Button variant="contained" onClick={handleSubmit}>
              Submit
            </Button>
          </Stack>
        </div>
      </Modal>
    </div>
  );
}

Overview.propTypes = {
  dates: PropTypes.object,
};

export default Overview;
